import { STANDARD_CODON_TABLE } from "@lib/geneticCodes";

/** Indicates whether a sequence is nucleotide or amino acid. */
type SequenceKind = "nt" | "aa";

/**
 * Standard genetic code codon table.
 * Maps three-letter codons to single-letter amino acid codes.
 * Stop codons map to "*".
 */
export const CODON_TABLE: Record<string, string> = STANDARD_CODON_TABLE;

/**
 * Maps single-letter amino acid codes to 3-letter names.
 */
export const AMINO_ACID_NAMES: Record<string, string> = {
  A: "Ala",
  C: "Cys",
  D: "Asp",
  E: "Glu",
  F: "Phe",
  G: "Gly",
  H: "His",
  I: "Ile",
  K: "Lys",
  L: "Leu",
  M: "Met",
  N: "Asn",
  P: "Pro",
  Q: "Gln",
  R: "Arg",
  S: "Ser",
  T: "Thr",
  V: "Val",
  W: "Trp",
  Y: "Tyr",
  "*": "Stop",
};

type AminoAcidInfo = {
  abbreviation: string;
  fullName: string;
  property: string;
};

/** Extended amino acid data: abbreviation, full name, and chemical property. */
const AMINO_ACID_INFO: Record<string, AminoAcidInfo> = {
  A: { abbreviation: "Ala", fullName: "Alanine", property: "Hydrophobic" },
  C: { abbreviation: "Cys", fullName: "Cysteine", property: "Polar" },
  D: { abbreviation: "Asp", fullName: "Aspartate", property: "Negative" },
  E: { abbreviation: "Glu", fullName: "Glutamate", property: "Negative" },
  F: {
    abbreviation: "Phe",
    fullName: "Phenylalanine",
    property: "Hydrophobic",
  },
  G: { abbreviation: "Gly", fullName: "Glycine", property: "Hydrophobic" },
  H: { abbreviation: "His", fullName: "Histidine", property: "Positive" },
  I: { abbreviation: "Ile", fullName: "Isoleucine", property: "Hydrophobic" },
  K: { abbreviation: "Lys", fullName: "Lysine", property: "Positive" },
  L: { abbreviation: "Leu", fullName: "Leucine", property: "Hydrophobic" },
  M: { abbreviation: "Met", fullName: "Methionine", property: "Hydrophobic" },
  N: { abbreviation: "Asn", fullName: "Asparagine", property: "Polar" },
  P: { abbreviation: "Pro", fullName: "Proline", property: "Hydrophobic" },
  Q: { abbreviation: "Gln", fullName: "Glutamine", property: "Polar" },
  R: { abbreviation: "Arg", fullName: "Arginine", property: "Positive" },
  S: { abbreviation: "Ser", fullName: "Serine", property: "Polar" },
  T: { abbreviation: "Thr", fullName: "Threonine", property: "Polar" },
  V: { abbreviation: "Val", fullName: "Valine", property: "Hydrophobic" },
  W: { abbreviation: "Trp", fullName: "Tryptophan", property: "Hydrophobic" },
  Y: { abbreviation: "Tyr", fullName: "Tyrosine", property: "Polar" },
  "*": { abbreviation: "Stop", fullName: "Stop codon", property: "Stop" },
};

type NucleotideInfo = {
  fullName: string;
  classification: string;
};

/** Full names and classifications for standard nucleotide bases. */
const NUCLEOTIDE_INFO: Record<string, NucleotideInfo> = {
  A: { fullName: "Adenine", classification: "Purine" },
  T: { fullName: "Thymine", classification: "Pyrimidine" },
  G: { fullName: "Guanine", classification: "Purine" },
  C: { fullName: "Cytosine", classification: "Pyrimidine" },
  U: { fullName: "Uracil", classification: "Pyrimidine" },
};

type IupacAmbiguityInfo = {
  bases: string;
  description: string;
};

/** Human-readable descriptions for IUPAC ambiguity codes. */
const IUPAC_AMBIGUITY_INFO: Record<string, IupacAmbiguityInfo> = {
  R: { bases: "A or G", description: "Purine" },
  Y: { bases: "C or T", description: "Pyrimidine" },
  S: { bases: "G or C", description: "Strong" },
  W: { bases: "A or T", description: "Weak" },
  K: { bases: "G or T", description: "Keto" },
  M: { bases: "A or C", description: "Amino" },
  B: { bases: "C, G, or T", description: "Not A" },
  D: { bases: "A, G, or T", description: "Not C" },
  H: { bases: "A, C, or T", description: "Not G" },
  V: { bases: "A, C, or G", description: "Not T" },
  N: { bases: "A, C, G, or T", description: "Any base" },
};

const COMPLEMENT: Record<string, string> = {
  A: "T",
  T: "A",
  G: "C",
  C: "G",
  U: "A",
};

/** IUPAC nucleotide ambiguity codes. Maps sorted base combinations to their code. */
const IUPAC_CODES: Record<string, string> = {
  AG: "R",
  GA: "R",
  CT: "Y",
  TC: "Y",
  GC: "S",
  CG: "S",
  AT: "W",
  TA: "W",
  GT: "K",
  TG: "K",
  AC: "M",
  CA: "M",
  CGT: "B",
  CTG: "B",
  GCT: "B",
  GTC: "B",
  TCG: "B",
  TGC: "B",
  AGT: "D",
  ATG: "D",
  GAT: "D",
  GTA: "D",
  TAG: "D",
  TGA: "D",
  ACT: "H",
  ATC: "H",
  CAT: "H",
  CTA: "H",
  TAC: "H",
  TCA: "H",
  ACG: "V",
  AGC: "V",
  CAG: "V",
  CGA: "V",
  GAC: "V",
  GCA: "V",
  ACGT: "N",
  ACTG: "N",
  AGCT: "N",
  AGTC: "N",
  ATCG: "N",
  ATGC: "N",
  CAGT: "N",
  CATG: "N",
  CGAT: "N",
  CGTA: "N",
  CTAG: "N",
  CTGA: "N",
  GACT: "N",
  GATC: "N",
  GCAT: "N",
  GCTA: "N",
  GTAC: "N",
  GTCA: "N",
  TACG: "N",
  TAGC: "N",
  TCAG: "N",
  TCGA: "N",
  TGAC: "N",
  TGCA: "N",
};

/**
 * Return the complement of a nucleotide sequence (non-reversed).
 * Non-complementable characters are preserved as-is.
 */
function complement(sequence: string): string {
  let result = "";
  for (let i = 0; i < sequence.length; i++) {
    const base = sequence[i];
    result += COMPLEMENT[base] ?? base;
  }
  return result;
}

/**
 * Return the reverse complement of a nucleotide sequence.
 * Non-complementable characters are preserved as-is.
 */
function reverseComplement(sequence: string): string {
  let result = "";
  for (let i = sequence.length - 1; i >= 0; i--) {
    const base = sequence[i];
    result += COMPLEMENT[base] ?? base;
  }
  return result;
}

/**
 * Return the reversed sequence without complementing.
 */
function reverse(sequence: string): string {
  let result = "";
  for (let i = sequence.length - 1; i >= 0; i--) {
    result += sequence[i];
  }
  return result;
}

/**
 * Transcribe DNA to RNA by replacing T with U.
 * Preserves case.
 */
function transcribe(sequence: string): string {
  return sequence.replace(/T/gi, (match) => (match === "T" ? "U" : "u"));
}

/**
 * Reverse transcribe RNA to DNA by replacing U with T.
 * Preserves case.
 */
function reverseTranscribe(sequence: string): string {
  return sequence.replace(/U/gi, (match) => (match === "U" ? "T" : "t"));
}

type Frame = 1 | 2 | 3 | -1 | -2 | -3;

const FRAMES: Frame[] = [-3, -2, -1, 1, 2, 3];
const FORWARD_FRAMES: Frame[] = [1, 2, 3];
const REVERSE_FRAMES: Frame[] = [-3, -2, -1];

/**
 * Translate a nucleotide sequence to amino acids in the specified reading frame.
 *
 * @param sequence - Nucleotide sequence (A, T, G, C, U)
 * @param frame - Reading frame: 1, 2, 3 (forward) or -1, -2, -3 (reverse complement)
 * @param codonTable - Codon-to-amino-acid mapping. Defaults to the standard genetic code.
 * @returns Amino acid sequence. Unknown codons become "X", stop codons become "*".
 */
function translate(
  sequence: string,
  frame: Frame,
  codonTable: Record<string, string> = CODON_TABLE,
): string {
  const upper = sequence.toUpperCase();
  const seq = frame < 0 ? reverseComplement(upper) : upper;
  const offset = Math.abs(frame) - 1;

  let result = "";
  for (let i = offset; i + 2 < seq.length; i += 3) {
    const codon = seq.slice(i, i + 3);
    result += codonTable[codon] ?? "X";
  }

  return result;
}

type OrfRange = {
  start: number;
  end: number;
  incomplete: boolean;
};

/**
 * Find ORF ranges in a translated amino acid sequence.
 * An ORF starts with M (methionine/start codon) and ends with * (stop codon).
 * Incomplete ORFs that start with M but reach the end of the sequence without
 * a stop codon are also included.
 * Returns amino acid indices (not nucleotide positions).
 */
function findOrfRanges(translation: string): OrfRange[] {
  const ranges: OrfRange[] = [];
  let orfStart: number | null = null;

  for (let i = 0; i < translation.length; i++) {
    const aa = translation[i];

    if (orfStart === null && aa === "M") {
      orfStart = i;
    } else if (orfStart !== null && aa === "*") {
      ranges.push({ start: orfStart, end: i, incomplete: false });
      orfStart = null;
    }
  }

  if (orfStart !== null) {
    ranges.push({
      start: orfStart,
      end: translation.length - 1,
      incomplete: true,
    });
  }

  return ranges;
}

/**
 * Format a base pair count as a human-readable string.
 * Uses bp for < 1,000, kbp for < 1,000,000, Mbp otherwise.
 */
function formatBp(length: number): string {
  if (length >= 1_000_000) {
    return `${(length / 1_000_000).toFixed(1)} Mbp`;
  }
  if (length >= 1_000) {
    return `${(length / 1_000).toFixed(1)} kbp`;
  }
  return `${length} bp`;
}

/** Maps IUPAC ambiguity codes to the bases they represent. */
const IUPAC_BASES: Record<string, string> = {
  R: "AG",
  Y: "CT",
  S: "GC",
  W: "AT",
  K: "GT",
  M: "AC",
  B: "CGT",
  D: "AGT",
  H: "ACT",
  V: "ACG",
  N: "ACGT",
};

/**
 * Calculate the degeneracy of a primer sequence containing IUPAC ambiguity
 * codes. Degeneracy is the number of unique sequences the primer represents.
 */
function calculateDegeneracy(sequence: string): number {
  let degeneracy = 1;

  for (const char of sequence.toUpperCase()) {
    const bases = IUPAC_BASES[char];
    if (bases) {
      degeneracy *= bases.length;
    }
  }

  return degeneracy;
}

/**
 * Convert a nucleotide search query to a regex pattern where IUPAC ambiguity
 * codes become character classes (e.g. N → [ACGT], R → [AG]).
 * Standard bases pass through as literals.
 */
function iupacToRegex(query: string): string {
  let pattern = "";

  for (const char of query.toUpperCase()) {
    const bases = IUPAC_BASES[char];
    pattern += bases ? `[${bases}]` : char;
  }

  return pattern;
}

/** The purine bases: adenine (A) and guanine (G). */
const PURINES = new Set(["A", "G"]);

/** The pyrimidine bases: cytosine (C) and thymine (T). */
const PYRIMIDINES = new Set(["C", "T"]);

/** Characters that only appear in amino acid sequences, not nucleotide. */
const AA_ONLY = new Set("EFIJLOPQZX*");

/** Get the IUPAC ambiguity code for a set of nucleotide bases. */
function getIupacCode(bases: Set<string>): string {
  if (bases.size === 0) return "N";
  if (bases.size === 1) return bases.values().next().value ?? "N";

  const sorted = Array.from(bases).sort().join("");
  return IUPAC_CODES[sorted] ?? "N";
}

/** Get the ambiguous character for a sequence kind ("N" for nucleotides, "X" for amino acids). */
function getAmbiguousChar(kind: SequenceKind): string {
  return kind === "nt" ? "N" : "X";
}

/**
 * Detect whether a sequence is nucleotide or amino acid by scanning for
 * amino acid-only characters.
 */
function detectSequenceType(sequence: string): SequenceKind | null {
  if (sequence.length === 0) {
    return null;
  }

  for (const char of sequence.toUpperCase()) {
    if (AA_ONLY.has(char)) {
      return "aa";
    }
  }

  return "nt";
}

/**
 * Get all codons that encode a given amino acid.
 *
 * @param aminoAcidName - 3-letter amino acid name (e.g., "Phe", "Leu")
 * @param codonTable - Codon-to-amino-acid mapping. Defaults to the standard genetic code.
 * @returns sorted array of codons
 */
function getCodonsForAminoAcid(
  aminoAcidName: string,
  codonTable: Record<string, string> = CODON_TABLE,
): string[] {
  return Object.entries(codonTable)
    .filter(([, code]) => AMINO_ACID_NAMES[code] === aminoAcidName)
    .map(([codon]) => codon)
    .sort();
}

export {
  AA_ONLY,
  AMINO_ACID_INFO,
  type AminoAcidInfo,
  calculateDegeneracy,
  complement,
  detectSequenceType,
  FORWARD_FRAMES,
  FRAMES,
  type Frame,
  findOrfRanges,
  formatBp,
  getAmbiguousChar,
  getCodonsForAminoAcid,
  getIupacCode,
  IUPAC_AMBIGUITY_INFO,
  IUPAC_BASES,
  type IupacAmbiguityInfo,
  iupacToRegex,
  NUCLEOTIDE_INFO,
  type NucleotideInfo,
  type OrfRange,
  PURINES,
  PYRIMIDINES,
  REVERSE_FRAMES,
  reverse,
  reverseComplement,
  reverseTranscribe,
  type SequenceKind,
  transcribe,
  translate,
};
